/**
 * #690: the enterprise-wide Memory read path's contract, shared by `apps/api`'s `MemoryGraphService`/
 * `MemoryGraphController` and the `apps/portal` client that draws their response — matching how other
 * request/response contracts in this OS (`WebhookDetail`, `ExecutionSummary`, …) live here once rather than
 * as two hand-kept copies. `MemoryGraphNode` and `MemoryGraphResponse` are not here: each side's `layer` field
 * is typed against what it can actually import (the API can name the real `MemoryLayer` union from `@zerohuman/db`;
 * the portal cannot), so those two stay defined once per side.
 */

/**
 * What one memory node *is*, distinct from what merely correlates with it. `fact` and `observation` are both
 * directly evidenced; `inference` is drawn from a pattern across other entries and must never be presented as
 * proof of anything on its own. `correction` supersedes an earlier entry rather than merely relating to it —
 * see `MemoryRelationshipType`.
 */
export type MemoryKnowledgeType = 'fact' | 'observation' | 'preference' | 'correction' | 'inference'

/**
 * What one edge between two memory nodes means. `similar-to` is a similarity/co-occurrence signal only — it
 * must never be read as `supersedes` or as evidence of causation (#690 AC3: "similarity is not presented as
 * proof").
 */
export type MemoryRelationshipType = 'shares-task' | 'shares-team' | 'supersedes' | 'similar-to'

/** Whether a node stands as retained knowledge, has been superseded by a correction, or excluded from Ask. */
export type MemoryCorrectionState = 'active' | 'corrected' | 'excluded'

/**
 * One team/project/task label a node carries, shown as organisation-wide grouping — never as private storage
 * (#690 AC4: "Team filters are clearly organisational and do not suggest private team storage").
 */
export type MemoryLabel = { kind: 'team' | 'project' | 'task'; id: string; name: string }

/** One labelled relationship between two nodes. */
export type MemoryGraphEdge = {
    id: string
    source: string
    target: string
    type: MemoryRelationshipType
}

/**
 * Who put a node into the enterprise's memory (#959): the OS (`trained` — Train, a correction, a backfill or an
 * import) or a run, which retains what it learned at the end of its work (`run`).
 */
export type MemoryNodeOrigin = 'trained' | 'run'

/** Search/team/project/type/time filters the graph and list views apply identically (#690 AC5). */
export type MemoryGraphQuery = {
    search?: string
    teamId?: string
    projectId?: string
    knowledgeType?: MemoryKnowledgeType
    from?: string
    to?: string
    /**
     * Whether knowledge runs retained is listed beside trained notes (#959). Off unless asked for, so the page
     * opens on the curated view, and nothing is hidden: one filter brings every run-learned node in.
     */
    includeRunLearned?: boolean
    page?: number
    limit?: number
}

/**
 * The minimal shape one of the three filter predicates below needs to judge a node — both `apps/api`'s
 * `MemoryGraphNode` (whose `layer` is the real `MemoryLayer` union) and `apps/portal`'s (whose `layer` is a
 * plain `string`) satisfy this structurally, so one predicate serves both without either side importing the
 * other's node type.
 */
export type MemoryFilterableNode = {
    body: string
    provenance: string | null
    labels: MemoryLabel[]
    createdAt: string
    /** Absent reads as `trained`: a node from before #959 said nothing about its origin, and the OS wrote it. */
    origin?: MemoryNodeOrigin
}

/**
 * Case-insensitive substring match on a node's body and its provenance — the two texts a reader searches by.
 * Shared by `apps/api`'s `MemoryGraphService.matching` and `apps/portal`'s `filterMemoryNodes` so the graph and
 * the list (and the API and its optimistic client-side re-filter) narrow a search identically (#690 AC5).
 */
export function matchesMemorySearch(node: MemoryFilterableNode, search: string): boolean {
    const needle = search.trim().toLowerCase()
    if (needle === '') return true
    return `${node.body} ${node.provenance ?? ''}`.toLowerCase().includes(needle)
}

/** Whether the node carries a label of this kind and id. Labels are organisational grouping, never privacy (#690 AC4). */
export function memoryNodeHasLabel(node: MemoryFilterableNode, kind: MemoryLabel['kind'], id: string): boolean {
    return node.labels.some(label => label.kind === kind && label.id === id)
}

/** Whether the node was created inside the (possibly half-open) time window the caller named. */
export function memoryNodeInTimeWindow(node: MemoryFilterableNode, from?: string, to?: string): boolean {
    const created = Date.parse(node.createdAt)
    if (Number.isNaN(created)) return true
    if (from && created < Date.parse(from)) return false
    if (to && created > Date.parse(to)) return false
    return true
}

/** The minimal shape `matchesMemoryQuery` needs beyond `MemoryFilterableNode`: the type its `knowledgeType` filter judges. */
export type MemoryQueryableNode = MemoryFilterableNode & { knowledgeType: MemoryKnowledgeType }

/**
 * Whether a node matches every filter the caller named in `query`. An absent filter narrows nothing; a named
 * one narrows exactly, so a `fact` is never returned for a `knowledgeType: 'inference'` query and vice versa
 * (#690 AC3). Shared by `apps/api`'s `MemoryGraphService.matching` and `apps/portal`'s `filterMemoryNodes` —
 * both composed these same four predicates identically before this was pulled out (#690 AC5).
 */
export function matchesMemoryQuery(node: MemoryQueryableNode, query: MemoryGraphQuery): boolean {
    if (!query.includeRunLearned && node.origin === 'run') return false
    if (query.search && !matchesMemorySearch(node, query.search)) return false
    if (query.knowledgeType && node.knowledgeType !== query.knowledgeType) return false
    if (query.teamId && !memoryNodeHasLabel(node, 'team', query.teamId)) return false
    if (query.projectId && !memoryNodeHasLabel(node, 'project', query.projectId)) return false
    if (!memoryNodeInTimeWindow(node, query.from, query.to)) return false
    return true
}
